/* Progressive enhancement: one readable session, with scroll, tabs and arrows. */
(() => {
  function init(root) {
    if (root.dataset.talkReady) return;
    const panels = [...root.querySelectorAll('[data-talk-panel]')];
    const tabs = [...root.querySelectorAll('[data-talk-pick]')];
    if (!panels.length || panels.length !== tabs.length) return;
    root.dataset.talkReady = 'true';
    const track = root.querySelector('[data-talk-track]');
    const workspace = root.querySelector('.cv-lab-talk__workspace');
    const tablist = root.querySelector('[data-talk-tabs]');
    const previous = root.querySelector('[data-talk-prev]');
    const next = root.querySelector('[data-talk-next]');
    const count = root.querySelector('[data-talk-count]');
    const meter = root.querySelector('[data-talk-meter]');
    const hint = root.querySelector('[data-talk-hint]');
    const scrollMode = matchMedia('(min-width: 1000px) and (min-height: 740px) and (prefers-reduced-motion: no-preference)');
    let active = -1;
    let queued = false;
    let pointer = null;
    const cleanups = [];
    function listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    }
    function paint(index) {
      index = Math.max(0, Math.min(panels.length - 1, index));
      if (active === index) return;
      active = index;
      panels.forEach((panel, i) => {
        panel.hidden = i !== active;
        tabs[i].setAttribute('aria-selected', String(i === active));
        tabs[i].tabIndex = i === active ? 0 : -1;
      });
      // Keep the selected tab visible without moving the document.
      const selected = tabs[active];
      const left = selected.offsetLeft - tablist.offsetLeft;
      if (left < tablist.scrollLeft) tablist.scrollLeft = left;
      else if (left + selected.offsetWidth > tablist.scrollLeft + tablist.clientWidth) {
        tablist.scrollLeft = left + selected.offsetWidth - tablist.clientWidth;
      }
      previous.disabled = active === 0;
      next.disabled = active === panels.length - 1;
      count.innerHTML = `${String(active + 1).padStart(2, '0')} <span>/ ${String(panels.length).padStart(2, '0')}</span>`;
      meter.style.width = `${((active + 1) / panels.length) * 100}%`;
    }
    function dimensions() {
      return {
        top: track.getBoundingClientRect().top + window.scrollY - parseFloat(getComputedStyle(workspace).top || 0),
        distance: Math.max(1, track.offsetHeight - workspace.offsetHeight)
      };
    }
    function readScroll() {
      if (!scrollMode.matches) return;
      const { top, distance } = dimensions();
      const progress = Math.max(0, Math.min(1, (window.scrollY - top) / distance));
      paint(Math.min(panels.length - 1, Math.floor(progress * panels.length)));
    }
    function choose(index) {
      index = Math.max(0, Math.min(panels.length - 1, index));
      paint(index);
      if (scrollMode.matches) {
        const { top, distance } = dimensions();
        // Land inside the selected interval, not on its rounding boundary.
        // Instant alignment keeps controls deterministic even during smooth scrolling.
        window.scrollTo({ top: top + distance * (index + 0.3) / panels.length, behavior: 'instant' });
      }
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture('demo_talk_turn', { turn: index + 1 });
      }
    }
    tablist.setAttribute('role', 'tablist');
    tabs.forEach((tab, i) => {
      tab.setAttribute('role', 'tab');
      panels[i].setAttribute('role', 'tabpanel');
      panels[i].tabIndex = 0;
      listen(tab, 'click', () => choose(i));
      listen(tab, 'keydown', event => {
        const keys = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: panels.length - 1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const target = Math.max(0, Math.min(panels.length - 1, keys[event.key]));
        choose(target);
        tabs[target].focus({ preventScroll: true });
      });
    });
    listen(previous, 'click', () => choose(active - 1));
    listen(next, 'click', () => choose(active + 1));
    // Horizontal touch gestures only; vertical page scrolling remains native.
    listen(workspace, 'pointerdown', event => {
      if (event.pointerType !== 'touch' || event.target.closest('button,a')) return;
      pointer = { x: event.clientX, y: event.clientY };
    }, { passive: true });
    listen(workspace, 'pointerup', event => {
      if (!pointer) return;
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      pointer = null;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) choose(active + (dx < 0 ? 1 : -1));
    }, { passive: true });
    listen(workspace, 'pointercancel', () => { pointer = null; });
    function configure() {
      root.classList.toggle('is-scroll-linked', scrollMode.matches);
      hint.textContent = scrollMode.matches ? 'Scroll to follow the conversation' : 'Choose a step to explore';
    }
    listen(scrollMode, 'change', () => { configure(); });
    listen(window, 'scroll', () => {
      if (!scrollMode.matches || queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        if (root.isConnected) readScroll();
      });
    }, { passive: true });
    listen(window, 'resize', () => { configure(); });
    root.classList.add('is-enhanced');
    configure();
    paint(0);
    readScroll();
    return () => {
      cleanups.forEach(cleanup => cleanup());
      delete root.dataset.talkReady;
    };
  }
  // Custom-element lifecycle also covers theme-editor and CLI section replacement.
  if (!customElements.get('cartivate-talk')) {
    customElements.define('cartivate-talk', class extends HTMLElement {
      connectedCallback() {
        queueMicrotask(() => {
          if (!this.isConnected || this.cleanup) return;
          const root = this.querySelector('[data-talk]');
          if (root) this.cleanup = init(root);
        });
      }
      disconnectedCallback() {
        this.cleanup?.();
        this.cleanup = null;
      }
    });
  }
})();
